/**
 * ActivityCommentInput - Input de comentário com @menções, anexos e drag & drop
 *
 * @version 1.3.0
 * RELEASE: 2026-02-09 v1.0.0 - Implementação inicial
 * RELEASE: 2026-04-11 v1.1.0 - Suporte a anexos (staged files)
 * RELEASE: 2026-04-11 v1.2.0 - Drag & drop, thumbnails reais, multi-file
 * RELEASE: 2026-06-05 v1.3.0 - Correção do fluxo de @menções: sincroniza
 *   cursorPosition em onSelect/onKeyUp/onClick do textarea; handleSelectMention
 *   lê selectionStart direto do DOM; dependências corretas dos useCallback;
 *   popover sem input interno (ver UserMentionPopover v2.0.0).
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Loader2, Paperclip, X, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { UserMentionPopover } from './UserMentionPopover';
import { useTenantMembers } from '@/hooks/useActivityComments';
import { validateFile } from '@/hooks/useTaskAttachments';
import { getFileIcon, getFileColor, formatFileSize } from '@/lib/file-utils';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

/** Props do componente */
interface ActivityCommentInputProps {
  onSubmit: (content: string, mentionedUserIds: string[], files?: File[]) => Promise<void>;
  isSubmitting: boolean;
  placeholder?: string;
  /** Habilita botão de anexar arquivos */
  allowAttachments?: boolean;
}

/** Interface para usuário mencionável */
interface MentionableUser {
  id: string;
  full_name: string;
  avatar_url: string | null;
}

/** Interface para arquivo staged com preview */
interface StagedFile {
  file: File;
  previewUrl: string | null;
}

/**
 * Input de comentário premium com @menções, drag & drop e thumbnails
 */
export function ActivityCommentInput({
  onSubmit,
  isSubmitting,
  placeholder = "Adicione um comentário...",
  allowAttachments = false,
}: ActivityCommentInputProps) {
  const [content, setContent] = useState('');
  const [mentionedUsers, setMentionedUsers] = useState<MentionableUser[]>([]);
  const [showMentionPopover, setShowMentionPopover] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);

  // Busca membros do tenant
  const { data: tenantMembers = [] } = useTenantMembers();

  // Cleanup de URLs de preview ao desmontar ou remover arquivos
  useEffect(() => {
    return () => {
      stagedFiles.forEach(sf => {
        if (sf.previewUrl) URL.revokeObjectURL(sf.previewUrl);
      });
    };
  }, []);

  /** Processa e valida arquivos (usado por input e drag & drop) */
  const processFiles = useCallback((files: File[]) => {
    const newStagedFiles: StagedFile[] = [];

    for (const file of files) {
      const validation = validateFile(file);
      if (!validation.valid) {
        toast.error(`${file.name}: ${validation.error}`);
      } else {
        const previewUrl = file.type.startsWith('image/')
          ? URL.createObjectURL(file)
          : null;
        newStagedFiles.push({ file, previewUrl });
      }
    }

    if (newStagedFiles.length > 0) {
      setStagedFiles(prev => [...prev, ...newStagedFiles]);
    }
  }, []);

  // Atualiza posição do cursor (usado em onSelect/onClick/onKeyUp do textarea)
  const syncCursor = useCallback(() => {
    const el = textareaRef.current;
    if (el) setCursorPosition(el.selectionStart ?? 0);
  }, []);

  // Detecta digitação de "@" para abrir popover
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      const cursor = e.target.selectionStart || 0;

      setContent(value);
      setCursorPosition(cursor);

      // Verifica se está digitando uma menção
      const textBeforeCursor = value.substring(0, cursor);
      const lastAtIndex = textBeforeCursor.lastIndexOf('@');

      if (lastAtIndex !== -1) {
        // Só dispara menção se o `@` estiver no início ou após espaço/quebra
        const charBeforeAt = lastAtIndex > 0 ? value[lastAtIndex - 1] : '';
        const validAtContext = lastAtIndex === 0 || /\s/.test(charBeforeAt);

        const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
        const isTypingNewMention = !textAfterAt.includes(' ') && !textAfterAt.includes('\n');
        const isExistingMention = mentionedUsers.some((u) =>
          textAfterAt.startsWith(u.full_name),
        );

        if (validAtContext && isTypingNewMention && !isExistingMention) {
          setMentionSearch(textAfterAt);
          setShowMentionPopover(true);
          return;
        }
      }

      setShowMentionPopover(false);
      setMentionSearch('');
    },
    [mentionedUsers],
  );

  // Seleciona usuário da menção
  const handleSelectMention = useCallback(
    (user: MentionableUser) => {
      // Lê a posição real do cursor diretamente do DOM (mais confiável que state).
      const liveCursor = textareaRef.current?.selectionStart ?? cursorPosition;
      const textBeforeCursor = content.substring(0, liveCursor);
      const lastAtIndex = textBeforeCursor.lastIndexOf('@');

      if (lastAtIndex !== -1) {
        const textBeforeAt = content.substring(0, lastAtIndex);
        const textAfterCursor = content.substring(liveCursor);

        const mentionText = `@${user.full_name} `;
        const newContent = textBeforeAt + mentionText + textAfterCursor;

        setContent(newContent);
        setMentionedUsers((prev) => [...prev.filter((u) => u.id !== user.id), user]);

        const newCursorPos = lastAtIndex + mentionText.length;
        // requestAnimationFrame garante que o re-render ocorreu antes do foco
        requestAnimationFrame(() => {
          const el = textareaRef.current;
          if (el) {
            el.focus();
            el.setSelectionRange(newCursorPos, newCursorPos);
            setCursorPosition(newCursorPos);
          }
        });
      }

      setShowMentionPopover(false);
      setMentionSearch('');
    },
    [content, cursorPosition],
  );

  // Fecha popover com Escape / submit com Ctrl+Enter
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowMentionPopover(false);
      }
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [content, mentionedUsers, stagedFiles],
  );

  /** Handler para selecionar arquivos via input */
  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    processFiles(files);

    // Limpa input para permitir selecionar mesmo arquivo novamente
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [processFiles]);

  /** Remove arquivo staged e libera URL de preview */
  const removeStagedFile = useCallback((index: number) => {
    setStagedFiles(prev => {
      const removed = prev[index];
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  // === Handlers de Drag & Drop ===
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!allowAttachments) return;
    dragCounterRef.current++;
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragging(true);
    }
  }, [allowAttachments]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounterRef.current = 0;
    if (!allowAttachments) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      processFiles(files);
    }
  }, [allowAttachments, processFiles]);

  // Submete o comentário
  const handleSubmit = async () => {
    const trimmedContent = content.trim();
    if ((!trimmedContent && stagedFiles.length === 0) || isSubmitting) return;

    try {
      // Reconstrói formato @[Nome](uuid) para cada menção antes de enviar ao banco
      let contentWithMentionIds = trimmedContent;
      for (const user of mentionedUsers) {
        const displayFormat = `@${user.full_name}`;
        const storageFormat = `@[${user.full_name}](${user.id})`;
        contentWithMentionIds = contentWithMentionIds.replace(displayFormat, storageFormat);
      }
      
      const filesToSend = stagedFiles.length > 0 ? stagedFiles.map(sf => sf.file) : undefined;
      
      await onSubmit(
        contentWithMentionIds, 
        mentionedUsers.map(u => u.id),
        filesToSend
      );

      // Limpa previews antes de resetar estado
      stagedFiles.forEach(sf => {
        if (sf.previewUrl) URL.revokeObjectURL(sf.previewUrl);
      });
      setContent('');
      setMentionedUsers([]);
      setStagedFiles([]);
    } catch (error) {
      // Erro tratado pelo hook
    }
  };

  // Filtra usuários para o popover
  const filteredUsers = tenantMembers.filter(user =>
    user.full_name.toLowerCase().includes(mentionSearch.toLowerCase())
  );

  return (
    <div
      className="relative"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Overlay de drag & drop */}
      {isDragging && (
        <div className="absolute inset-0 z-20 rounded-lg border-2 border-dashed border-primary bg-primary/5 flex items-center justify-center pointer-events-none animate-pulse">
          <div className="flex flex-col items-center gap-1 text-primary">
            <Upload className="h-5 w-5" />
            <span className="text-xs font-medium">Solte os arquivos aqui</span>
          </div>
        </div>
      )}

      {/* Grid premium de arquivos staged */}
      {stagedFiles.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {stagedFiles.map((staged, index) => {
            const isImage = staged.file.type.startsWith('image/');
            const fileType = isImage ? 'image' : staged.file.type.includes('pdf') ? 'pdf' : 
              staged.file.type.includes('spreadsheet') || staged.file.type.includes('excel') ? 'spreadsheet' : 
              staged.file.type.includes('word') || staged.file.type.includes('document') ? 'document' : 'other';
            const FileIcon = getFileIcon(fileType);
            const colorClasses = getFileColor(fileType);

            return (
              <div
                key={`${staged.file.name}-${index}`}
                className="relative group w-16 h-16 rounded-lg border bg-background overflow-hidden flex items-center justify-center transition-all hover:shadow-md"
              >
                {/* Thumbnail real para imagens, ícone para outros */}
                {isImage && staged.previewUrl ? (
                  <img
                    src={staged.previewUrl}
                    alt={staged.file.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className={cn('p-2 rounded', colorClasses)}>
                    <FileIcon className="h-6 w-6" />
                  </div>
                )}

                {/* Overlay com botão remover */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-0.5">
                  <button
                    type="button"
                    onClick={() => removeStagedFile(index)}
                    className="p-1 rounded-full bg-destructive/80 hover:bg-destructive transition-colors"
                    title="Remover arquivo"
                  >
                    <X className="h-3 w-3 text-white" />
                  </button>
                </div>

                {/* Tooltip com nome e tamanho */}
                <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-30">
                  <div className="bg-popover text-popover-foreground text-[9px] px-2 py-1 rounded shadow-lg whitespace-nowrap max-w-[140px] truncate border">
                    {staged.file.name}
                    <span className="text-muted-foreground ml-1">
                      ({formatFileSize(staged.file.size)})
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Textarea com menções */}
      <div className="flex gap-2 items-end">
        <div className="relative flex-1">
          <Textarea
            ref={textareaRef}
            value={content}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onKeyUp={syncCursor}
            onClick={syncCursor}
            onSelect={syncCursor}
            placeholder={placeholder}
            className={cn(
              "min-h-[36px] max-h-[120px] resize-none text-xs py-2 pr-2",
              "focus-visible:ring-1",
              isDragging && "border-primary ring-1 ring-primary/30"
            )}
            rows={1}
            disabled={isSubmitting}
          />
          
          {/* Popover de menções */}
          <UserMentionPopover
            isOpen={showMentionPopover}
            onClose={() => setShowMentionPopover(false)}
            onSelect={handleSelectMention}
            users={filteredUsers}
            searchTerm={mentionSearch}
            anchorRef={textareaRef}
          />
        </div>

        {/* Botão de anexar arquivo com badge de contagem */}
        {allowAttachments && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={handleFileSelect}
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
            />
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 flex-shrink-0 text-muted-foreground hover:text-foreground hover:bg-muted/50 relative"
              onClick={() => fileInputRef.current?.click()}
              disabled={isSubmitting}
              type="button"
              title="Anexar arquivo"
            >
              <Paperclip className="h-4 w-4" />
              {/* Badge de contagem de arquivos */}
              {stagedFiles.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-[9px] font-bold rounded-full h-4 w-4 flex items-center justify-center">
                  {stagedFiles.length}
                </span>
              )}
            </Button>
          </>
        )}

        {/* Botão de enviar */}
        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8 flex-shrink-0 text-primary hover:bg-primary/10"
          onClick={handleSubmit}
          disabled={isSubmitting || (!content.trim() && stagedFiles.length === 0)}
        >
          {isSubmitting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Hint de atalho e drag & drop */}
      <p className="text-[10px] text-muted-foreground mt-1">
        <kbd className="px-1 py-0.5 bg-muted rounded text-[9px]">Ctrl+Enter</kbd> enviar
        {allowAttachments && (
          <span className="ml-1.5">• Arraste arquivos aqui</span>
        )}
      </p>
    </div>
  );
}
